import * as React from 'react';
import { MenuIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BrandWordmark } from '@/components/BrandWordmark';
import { cn } from '@/lib/utils';
import { DIRECTORY_NAME, PRIMARY_CITY_NAME } from '@/lib/brand';

type NavItem = {
  label: string;
  href: string;
};

const navLinks: NavItem[] = [
  { label: 'Explore', href: '/directory' },
  { label: 'Journal', href: '/journal' },
  { label: 'About', href: '/about' },
  { label: 'Submit Venue', href: '/submit-venue' },
];

interface FloatingHeaderProps {
  fixed?: boolean;
}

export function FloatingHeader({ fixed = true }: FloatingHeaderProps) {
  const [open, setOpen] = React.useState(false);
  const [solidHeaderOpacity, setSolidHeaderOpacity] = React.useState(0);
  const location = useLocation();
  const isHome = location.pathname === '/';

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    if (!isHome) {
      setSolidHeaderOpacity(1);
      return;
    }

    const onScroll = () => {
      const nextOpacity = Math.min(window.scrollY / 72, 1);
      setSolidHeaderOpacity(nextOpacity);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const showTransparentStyle = isHome && solidHeaderOpacity < 0.55;

  const isActive = (href: string) =>
    href === '/'
      ? location.pathname === href
      : location.pathname === href || location.pathname.startsWith(`${href}/`);

  return (
    <header
      className={cn(
        fixed ? 'fixed left-0 right-0 top-0' : 'absolute left-0 right-0 top-0',
        'z-50 transition-colors duration-300',
      )}
    >
      <div
        className={cn(
          'relative border-b border-transparent px-4 sm:px-6',
        )}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 border-b border-white/10 transition-opacity duration-500 ease-out"
          style={{ opacity: 1 - solidHeaderOpacity }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 border-b border-brand-charcoal/8 bg-brand-offwhite/92 backdrop-blur-md transition-opacity duration-500 ease-out"
          style={{ opacity: solidHeaderOpacity }}
        />
        <div className="site-shell">
          <div className="relative z-10 flex min-h-[72px] items-center justify-between gap-6">
            <BrandWordmark
              href="/"
              dark={showTransparentStyle}
              className="gap-2.5"
              iconClassName="h-9 w-9 rounded-lg"
              showText={false}
              textClassName={cn(
                'text-[1rem] sm:text-[1.08rem]',
                showTransparentStyle
                  ? 'text-[#6D7D5C] drop-shadow-[0_1px_10px_rgba(255,255,255,0.22)]'
                  : 'text-[#6D7D5C]',
              )}
            />

            <nav className="hidden items-center gap-7 lg:flex">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    link.href === '/submit-venue'
                      ? 'inline-flex items-center rounded-[1rem] border px-4 py-2 text-sm font-semibold transition-all duration-300'
                      : 'text-sm font-medium transition-colors',
                    link.href === '/submit-venue'
                      ? showTransparentStyle
                        ? 'border-white/10 bg-[#1c1d1a]/72 bg-[linear-gradient(180deg,rgba(12,12,12,0.74),rgba(12,12,12,0.68)),linear-gradient(180deg,rgba(163,177,138,0.03),rgba(163,177,138,0.015)),url(\'/images/green%20variant.png\')] bg-cover bg-center text-brand-offwhite/82 shadow-[0_10px_24px_rgba(0,0,0,0.12)] hover:-translate-y-[1px] hover:border-white/14 hover:text-brand-offwhite hover:shadow-[0_14px_30px_rgba(0,0,0,0.18)]'
                        : 'border-white/12 bg-[#1c1d1a] bg-[linear-gradient(180deg,rgba(12,12,12,0.92),rgba(12,12,12,0.88)),linear-gradient(180deg,rgba(163,177,138,0.04),rgba(163,177,138,0.02)),url(\'/images/green%20variant.png\')] bg-cover bg-center text-brand-offwhite shadow-[0_12px_28px_rgba(0,0,0,0.18)] hover:-translate-y-[1px] hover:border-white/16 hover:shadow-[0_14px_32px_rgba(0,0,0,0.2)]'
                      : showTransparentStyle
                        ? 'text-brand-offwhite/78 hover:text-brand-offwhite'
                        : 'text-brand-charcoal/62 hover:text-brand-charcoal',
                    isActive(link.href) &&
                      (link.href === '/submit-venue'
                        ? 'border-white/16 bg-[#1c1d1a] bg-[linear-gradient(180deg,rgba(12,12,12,0.88),rgba(12,12,12,0.82)),linear-gradient(180deg,rgba(163,177,138,0.07),rgba(163,177,138,0.04)),url(\'/images/green%20variant.png\')] text-brand-offwhite'
                        : showTransparentStyle
                          ? 'text-brand-offwhite'
                          : 'text-brand-charcoal'),
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <button
              type="button"
              onClick={() => setOpen(true)}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-xl border transition-colors lg:hidden',
                showTransparentStyle
                  ? 'border-white/14 bg-white/10 text-brand-offwhite hover:bg-white/14'
                  : 'border-brand-charcoal/10 bg-white/84 text-brand-charcoal hover:bg-white',
              )}
              aria-label="Open navigation menu"
            >
              <MenuIcon className="size-4" />
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetContent className="gap-0" showClose side="left">
                <SheetHeader>
                  <BrandWordmark href="/" className="gap-2.5" iconClassName="h-10 w-10 rounded-lg" showText={false} textClassName="text-[#6D7D5C]" />
                  <SheetTitle className="sr-only">{DIRECTORY_NAME}</SheetTitle>
                  <SheetDescription>
                    Explore saunas, cold plunge, and contrast therapy across {PRIMARY_CITY_NAME} and beyond.
                  </SheetDescription>
                </SheetHeader>
                <div className="grid gap-y-1 px-4 py-5">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className={cn(
                        link.href === '/submit-venue'
                          ? 'rounded-[1rem] border border-white/12 bg-[#1c1d1a] bg-[linear-gradient(180deg,rgba(12,12,12,0.92),rgba(12,12,12,0.88)),linear-gradient(180deg,rgba(163,177,138,0.04),rgba(163,177,138,0.02)),url(\'/images/green%20variant.png\')] bg-cover bg-center px-3 py-3 text-base font-semibold text-brand-offwhite shadow-[0_12px_28px_rgba(0,0,0,0.18)] transition-colors hover:border-white/16'
                          : 'rounded-xl px-3 py-3 text-base font-medium transition-colors',
                        link.href !== '/submit-venue' && (
                          isActive(link.href)
                            ? 'bg-white text-brand-charcoal'
                            : 'text-brand-charcoal/80 hover:bg-white hover:text-brand-charcoal'
                        ),
                      )}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
